<template>
  <section>
    <callout
      :border-color="getStatusColor(value.statusId)"
      :controlled="true"
      :opened="calloutOpened"
      @click:open="getDetails"
    >
      <div class="row p-3">
        <div class="col-sm-12 col-md-2 border-right col-balloon">
          <div class="col-sm mb-4">
            <balloon
              :bg="
                `border-none text-center bg-${getStatusColor(value.statusId)}`
              "
              class="mt-2 font-15"
              dot
              :text="value.status"
            />
          </div>
          <div class="row">
            <div class="col ml-sm-none">
              <strong>{{ value.referralNumber }}</strong>
            </div>
          </div>
          <div class="row">
            <div class="col font-weight-normal ml-sm-none">
              <p>{{ value.disciplineName }}</p>
            </div>
          </div>
          <div v-if="value.daysCount" class="row">
            <div class="col font-weight-normal ml-sm-none">
              <p>
                Días de Vencimiento:
                <span class="rounded-pill bg-primary-17 text-white p-1">
                  {{ value.daysCount }}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div class="col-sm">
          <div class="row mb-2">
            <div class="col-sm-12 col-md-5">
              <span class="data-label">Estudiante:</span>
              <p class="data">
                {{
                  `${(value.fullName || '').replace(/[0-9]/g, '')} - ${
                    value.studentSieId
                  }`
                }}
              </p>
            </div>
            <div class="col-sm">
              <span class="data-label">Edad:</span>
              <p class="data">{{ value.studentAgeFormatted }} años</p>
            </div>
            <div
              v-if="userHasPermissions('provider.referral.center.change')"
              class="col-sm"
            >
              <validation-provider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="csee"
                  :clearable="false"
                  :error="errors[0]"
                  fieldkey="id"
                  fieldname="name"
                  label="CSEE:"
                  :options="centers"
                  placeholder="Seleccione"
                  @input="onChangeCenterHandler"
                />
              </validation-provider>
            </div>
            <div v-else class="col-sm">
              <span class="data-label">CSEE:</span>
              <p class="data">
                {{ (csee || { name: '-' }).name }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12 col-md-5">
              <span class="data-label">Modalidad:</span>
              <p class="data">
                {{ value.modalityTypeName }}
              </p>
            </div>
            <div class="col-sm">
              <span class="data-label">Frecuencia:</span>
              <p class="data">
                {{ value.frequecy }}
              </p>
            </div>
            <div class="col-sm">
              <span class="data-label">Duración:</span>
              <p class="data">
                {{ value.duration }}
              </p>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12 col-md-5">
              <span class="data-label">Nota de Admisión:</span>
              <p class="data">
                {{ this.hasAdmissionNote ? 'Sí' : 'No' }}
              </p>
            </div>
            <div class="col-sm">
              <span class="data-label">Servicio Coordinado:</span>
              <p class="data">
                {{ value.coordinatedService ? 'Sí' : 'No' }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="col-sm-12 col-md-2 mr-3 d-flex flex-column align-items-md-end align-items-sm-start justify-content-md-start"
        >
          <DropDown
            class="responsive-dropdown"
            :items="getMenuAvailableItems"
          />

          <div
            v-if="showAdmissionAbsenseCount"
            class="d-flex align-items-center mt-0 mt-md-4 mt-sm-0 pt-5"
          >
            <span class="font-weight-normal font-14 color-secondary-6 pr-1">
              Ausencia (s) de Admisión:
            </span>
            <span class="font-weight-normal font-16 color-primary-2">
              {{ value.admissionAbsenceCount }}
            </span>
          </div>
          <div
            v-if="showInterventionPlanAbsenseCount"
            class="d-flex align-items-center mt-0 mt-md-4 mt-sm-0 pt-5"
          >
            <span class="font-weight-normal font-14 color-secondary-6 pr-1">
              Ausencia de Plan:
            </span>
            <span class="font-weight-normal font-16 color-primary-2">
              {{ value.interventionPlanAbsenceCount }}
            </span>
          </div>
        </div>
      </div>

      <template #edition>
        <template v-if="calloutMode === CALLOUT_MODES.ASSIGN_SPECIALIST">
          <assign-specialist
            :dispose="
              () => {
                calloutMode = CALLOUT_MODES.STUDENT_INFORMATION;
                calloutOpened = false;
              }
            "
            :save="saveAssignment"
            :selected-specialist-id="value.assignmentSpecialistId"
            :specialists="assignSpecialists"
          />
        </template>
        <template v-else-if="calloutMode === CALLOUT_MODES.CONFIRM_ADMISSION">
          <confirm-admission
            v-model="value"
            :admission-info="admissionInfo"
            :dispose="
              () => {
                calloutMode = CALLOUT_MODES.STUDENT_INFORMATION;
                calloutOpened = false;
              }
            "
            :has-pilot-permission="hasPilotPermission"
            :save="saveConfirm"
            :update="updateConfirm"
          />
        </template>
        <template v-else-if="calloutMode === CALLOUT_MODES.VISIT_ABSENCE">
          <visit-absence
            :data="absence"
            :dispose="
              () => {
                calloutMode = CALLOUT_MODES.STUDENT_INFORMATION;
                calloutOpened = false;
              }
            "
            @save="saveAdsence"
          />
        </template>
        <template v-else-if="calloutMode === CALLOUT_MODES.DENY_BY_PROVIDER">
          <DenyByProvider
            :item="value"
            @finished="
              () => {
                calloutMode = CALLOUT_MODES.STUDENT_INFORMATION;
                calloutOpened = false;
              }
            "
            @refresh="
              () => {
                $emit('refreshReferralGrid');
              }
            "
          />
        </template>
        <template
          v-else-if="
            calloutMode === CALLOUT_MODES.STUDENT_INFORMATION ||
              calloutMode === CALLOUT_MODES.SERVICE_OFFERING_CURRENTYEAR ||
              calloutMode === CALLOUT_MODES.DIAGNOSIS
          "
        >
          <b-card class="bg-transparent border-0 p-2" no-body>
            <b-card class="bg-white border-0 p-3" no-body>
              <b-tabs
                v-model="tabsModel"
                class="mipe-green-tabs"
                content-class="pt-4"
                no-nav-style
              >
                <b-tab active title="Información General">
                  <InformationDetail
                    :has-pilot-permission="hasPilotPermission"
                    :value="value"
                  />
                </b-tab>
                <b-tab
                  v-if="canAddDiagnosisInPilot"
                  :active="tabsModel === 1"
                  lazy
                  title="Diagnóstico"
                  @click="addDiagnosis"
                >
                  <add-diagnosis :item="value" @cancel="onDiagnosisCancel" />
                </b-tab>
                <b-tab lazy title="Intervenciones Adeudadas">
                  <InterventionsDue
                    :academic-year-id="value.academicYearId"
                    :discipline-name="value.disciplineName"
                    :discipline-type-id="value.disciplineTypeId"
                    :referral-number="value.referralNumber"
                    :service-specialist-name="value.serviceSpecialist"
                    :student-name="value.fullName"
                    :student-sie-id="value.studentSieId"
                  />
                </b-tab>
                <b-tab
                  :active="tabsModel === 3"
                  lazy
                  title="Ofrecimiento de Servicios Año Actual"
                  @click="serviceOfferingCurrentYear(value)"
                >
                  <ServiceOfferingCurrentYear
                    :data="value.serviceOfferingData"
                    :dispose="
                      () => {
                        calloutMode =
                          CALLOUT_MODES.SERVICE_OFFERING_CURRENTYEAR;
                        calloutOpened = false;
                      }
                    "
                    @print="onPrint(value)"
                  />
                </b-tab>
              </b-tabs>
            </b-card>
          </b-card>
        </template>
      </template>
    </callout>
  </section>
</template>

<script>
import CustomDropDown from '@/components/common/form/CustomDropDown';
import AddDiagnosis from '@/components/common/AddDiagnosis';
import InformationDetail from './InformationDetail';
import AssignSpecialist from './AssignSpecialist';
import ConfirmAdmission from './ConfirmAdmission';
import DenyByProvider from './DenyByProvider';
import InterventionsDue from './InterventionsDue';
import VisitAbsence from '@/views/provider/referral-management/search/VisitAbsence.vue';
import {
  getReferralDetails as _getReferralDetails,
  updateBillingCenter as _updateBillingCenter,
  createProvisionalRemedy as _createProvisionalRemedy,
  approveProvisionalRemedy as _approveProvisionalRemedy,
  approvalLetterDownload as _approvalLetterDownload,
  downloadProvissionalRemedyRequestReport as _downloadProvissionalRemedyRequestReport,
  getReferralSheet as _getReferralSheet,
  getSpecialistsForAssignment,
  saveAssignment as _saveAssignment,
  getAdmissionInfo,
  getStudentReferralAdmision,
  savePartialAdmission,
  updateAdmission,
  getAbsenceReason,
  addAbsence,
  getLatestSignedPeipReport as _getLatestSignedPeipReport,
  confirmAdmission,
  getStudentServiceOffering,
  canCoordinateService as _canCoordinateService,
} from '@/modules/shared/studentreferral.api';
import { mapGetters } from 'vuex';
import statusColorMixin from '@/views/provider/referral-management/common/statusColor';
import alert from '@/mixins/alert.mixin';
import AdmissionStatus from '@/utils/shared/constants/admissionStatus';
import AdmissionDropTypes from '@/utils/constants/admissionDropTypes';
import _generalApi from '@/modules/app/api';
import storeMixin from '@/shared/mixins/storeGenerator';
import ServiceOfferingCurrentYear from './components/ServiceOfferingCurrentYear';
import { getSignatureHubInfo as _getSignatureHubInfo } from '@/modules/signatureHub/api';
import signatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import ReferralTypes from '@/utils/shared/constants/referralTypes';

const SPECIALIST_AVAILABILITY_CALENDAR_TRANSFER =
  'SpecialistAvailabilityCalendarTransfer';
const SPECIALIST_AVAILABILITY_CALENDAR_COORDINATION =
  'SpecialistAvailabilityCalendarCoordination';
const ADMISSION_MANAGEMENT_ROUTE_NAME = 'admissionManagement';
const CALLOUT_MODES = {
  STUDENT_INFORMATION: 'STUDENT_INFORMATION',
  ASSIGN_SPECIALIST: 'ASSIGN_SPECIALIST',
  CONFIRM_ADMISSION: 'CONFIRM_ADMISSION',
  VISIT_ABSENCE: 'VISIT_ABSENCE',
  DENY_BY_PROVIDER: 'DENY_BY_PROVIDER',
  SERVICE_OFFERING_CURRENTYEAR: 'SERVICE_OFFERING_CURRENTYEAR',
  DIAGNOSIS: 'DIAGNOSIS',
};

export default {
  name: 'RowReferral',
  components: {
    CustomDropDown,
    InformationDetail,
    AssignSpecialist,
    ConfirmAdmission,
    DenyByProvider,
    InterventionsDue,
    VisitAbsence,
    ServiceOfferingCurrentYear,
    AddDiagnosis,
  },
  mixins: [alert, statusColorMixin, storeMixin],
  props: {
    centers: { type: Array, default: () => [] },
    futureAcademicYear: { type: Object, default: () => {} },
    currentAcademicYearId: { type: Number, default: null },
    canAddResultSummary: { type: Boolean, default: false },
    hasPilotPermission: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => {
        return {
          admissionDate: null,
          admissionSpecialist: null,
          asigment: null,
          billingCenterId: null,
          billingCenterName: null,
          contact: null,
          contactEmail: null,
          contactTelephone: null,
          dateFrom: null,
          dateTo: null,
          duration: null,
          frequecy: null,
          fullName: null,
          impediment: null,
          initialDate: null,
          lastDateVisit: null,
          linkedDate: null,
          locationName: null,
          modalityTypeName: null,
          providerName: null,
          referralDate: null,
          school: null,
          studentAge: null,
          studentSieId: null,
          admissionCreationReason: null,
          noteId: null,
        };
      },
    },
    allowedGoToRecord: {
      type: Boolean,
      default: () => false,
    },
    canAddDiagnosis: {
      type: Boolean,
      default: () => false,
    },
    medicaidPilotCompanyIds: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    calloutOpened: false,
    calloutMode: CALLOUT_MODES.INFORMATION,
    CALLOUT_MODES,
    csee: null,
    assignSpecialists: [],
    lastCsee: null,
    admissionInfo: {},
    absence: {
      reasons: [],
      linkedDate: '',
    },
    tabsModel: 0,
  }),
  computed: {
    ...mapGetters('$_user', ['fullProfile', 'companies']),
    hasAdmissionNote() {
      return this.value?.noteId != null;
    },
    isInitialReferralType() {
      return this.value.admissionCreationReasonId == ReferralTypes.Initial;
    },
    isSpecialist() {
      return this.fullProfile?.specialistId != null;
    },
    canGoToAdmissionNote() {
      const dropTypeIdsNoAllowed = [
        AdmissionDropTypes.Alta,
        AdmissionDropTypes.Baja,
      ];
      return (
        this.showIfContainStatus([
          AdmissionStatus.Admitido,
          AdmissionStatus.ServicioRelacionadoActivo,
          AdmissionStatus.NoVigenteCambioServicio,
          AdmissionStatus.NoVigenteAlta,
          AdmissionStatus.NoVigenteEgreso,
        ]) ||
        (this.hasAdmissionNote &&
          !dropTypeIdsNoAllowed.includes(this.value.dropTypeId))
      );
    },
    hasInterventionPlan() {
      return (
        this.value.hasInterventionPlan != null &&
        this.value.hasInterventionPlan > 0
      );
    },
    hasSummary() {
      return this.value.hasSummary != null && this.value.hasSummary > 0;
    },
    getMenuAvailableItems() {
      return [
        {
          label: 'Descargar PEI',
          icon: 'fas fa-print color-primary-1',
          click: async () => {
            await _getLatestSignedPeipReport(this.value.studentSieId).then(
              (res) => {
                _generalApi.documentDownloadReport(
                  res.data.content,
                  res.data.filename
                );
              }
            );
          },
        },
        {
          label: 'Ver Documentos',
          icon: 'fas fa-file-alt color-primary-1',
          visible: !this.allowedGoToRecord,
          click: () => this.goToDocuments(this.value),
        },
        {
          label: 'Ver Expediente',
          icon: 'fas fa-file-alt color-primary-1',
          visible: this.allowedGoToRecord,
          click: () => this.goToRecord(this.value),
        },
        {
          label: 'Ver Hoja de Referido',
          border: 'bottom',
          icon: 'fas fa-file-download color-primary-1',
          click: () => {
            this.downloadReferralSheet(this.value);
          },
        },
        {
          label: 'Confirmar',
          icon: 'fas fa-check-square color-primary-1',
          visible:
            this.userHasPermissions('provider.referral.confirm') &&
            this.showIfContainStatus([AdmissionStatus.Asignado]) &&
            this.value.academicYearId == this.currentAcademicYearData.id,
          click: this.confirm,
        },
        {
          label: 'Denegar',
          icon: 'fas fa-times-square color-primary-3',
          visible:
            ([AdmissionStatus.Asignado, AdmissionStatus.Vinculado].includes(
              this.value.statusId
            ) &&
              this.value.academicYearId == this.currentAcademicYearData.id) ||
            ([AdmissionStatus.Asignado, AdmissionStatus.Vinculado].includes(
              this.value.statusId
            ) &&
              this.prevYearIsSelected &&
              this.userHasPermissions(
                'provider.referralmanagement.denypreviousyear'
              )),
          click: () => {
            this.calloutMode = this.CALLOUT_MODES.DENY_BY_PROVIDER;
            this.calloutOpened = true;
          },
        },
        {
          label: 'Añadir Documentos',
          icon: 'fas fa-files-medical color-primary-1',
          border: 'bottom',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.NoVigenteTraslado,
              AdmissionStatus.ServicioRelacionadoActivo,
              AdmissionStatus.NoVigenteBajas,
              AdmissionStatus.NoVigenteAlta,
              AdmissionStatus.NoVigenteEgreso,
              AdmissionStatus.Admitido,
            ]) && this.userHasPermissions('referredmanagement.linkonaddplan'),
          click: () => {
            this.addPlan(this.value);
          },
        },
        {
          label: 'Descargar Solicitud',
          icon: 'fas fa-download color-primary-1',
          visible: this.showIfContainStatus([
            AdmissionStatus.AprobadoRemedioProvisional,
          ]),
          click: this.downloadProvissionalRemedyRequestReport,
        },
        {
          label: 'Descargar Carta de Aprobación',
          border: 'bottom',
          icon: 'fas fa-download color-primary-1',
          visible: this.showIfContainStatus([
            AdmissionStatus.AprobadoRemedioProvisional,
          ]),
          click: () => this.approvalletterdownload(this.value),
        },
        {
          label: 'Historial de Referido',
          icon: 'fas fa-history color-primary-1',
          click: () => {
            this.$router.push({
              name: 'referralHistory',
              params: {
                referralNumber: this.value.referralNumber,
                admissionId: this.value.admissionId,
              },
            });
          },
        },
        {
          label: 'Ausencia de Admisión',
          icon: 'fas fa-user-cog color-primary-1',
          visible:
            (this.userHasPermissions('referralmanagement.addAbsence') &&
              this.showIfContainStatus([AdmissionStatus.Asignado]) &&
              this.value.academicYearId == this.currentAcademicYearData.id) ||
            (this.userHasPermissions('referralmanagement.addAbsence') &&
              this.showIfContainStatus([AdmissionStatus.Asignado]) &&
              this.prevYearIsSelected &&
              this.userHasPermissions(
                'provider.referralmanagement.absenceadmissionpreviousyear'
              )),
          click: this.visitAbsence,
        },
        {
          label: 'Historial de Visitas',
          icon: 'fas fa-user-clock color-primary-1',
          click: () => this.$emit('onShowVisitHistory', this.value.admissionId),
        },
        {
          label: 'Crear Solicitud de Remedio Provisional',
          icon: 'fas fa-plus-circle color-primary-1',
          visible:
            this.showIfContainStatus([AdmissionStatus.ReferidoDirectorCSEE]) &&
            this.userHasPermissions('referredmanagement.remedy'),
          click: this.createProvisionalRemedy,
        },
        {
          label: 'Asignar',
          icon: 'fas fa-plus-square color-primary-1',
          visible: this.canSelfAssign(this.value),
          click: this.assign,
        },
        {
          label: 'Manejar Admisión',
          icon: 'fas fa-clipboard-user color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
            ]) && this.value.academicYearId == this.currentAcademicYearId,
          click: () => {
            this.goToReferralManagement(this.value);
          },
        },
        {
          label: this.hasAdmissionNote
            ? 'Ver Nota de Admisión'
            : 'Añadir Nota de Admisión',
          icon: 'far fa-money-check-edit color-primary-1',
          visible: this.canGoToAdmissionNote,
          click: () => {
            this.$router.push({
              name: 'admissionNote',
              params: {
                admissionId: this.value.admissionId,
              },
            });
          },
        },
        {
          label: 'Ver Plan',
          icon: 'fas fa-files-medical color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
              AdmissionStatus.Asignado,
              AdmissionStatus.NoVigenteEgreso,
              AdmissionStatus.NoVigenteAlta,
              AdmissionStatus.NoVigenteCambioServicio,
            ]) && this.hasInterventionPlan,
          click: () => {
            this.$router.push({
              name: 'interventionPlan',
              params: {
                admissionId: this.value.admissionId,
                academicYearId: this.value.academicYearId,
                interventionPlanId: this.value.hasInterventionPlan,
                dateIntervention: this.value.dateFrom,
              },
            });
          },
        },
        {
          label: 'Añadir Plan',
          icon: 'fas fa-files-medical color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
              AdmissionStatus.Asignado,
              AdmissionStatus.NoVigenteEgreso,
              AdmissionStatus.NoVigenteAlta,
              AdmissionStatus.NoVigenteCambioServicio,
            ]) &&
            !this.hasInterventionPlan &&
            (this.value.academicYearId == this.currentAcademicYearData.id ||
              (this.prevYearIsSelected &&
                this.userHasPermissions(
                  'provider.referralmanagement.addinterventionplan'
                ))),
          click: async () => {
            await this.getDetails();

            this.$router.push({
              name: 'interventionPlan',
              params: {
                admissionId: this.value.admissionId,
                academicYearId: this.value.academicYearId,
                interventionPlanId: this.value.hasInterventionPlan,
                dateIntervention: this.value.dateFrom,
              },
            });
          },
        },
        {
          label:
            (this.value.interventionPlanNextYearId
              ? 'Ver Plan '
              : 'Añadir Plan ') + this.futureAcademicYear.description,
          icon: 'fas fa-files-medical color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
              AdmissionStatus.Asignado,
              AdmissionStatus.NoVigenteEgreso,
              AdmissionStatus.NoVigenteCambioServicio,
            ]) && this.userHasPermissions('interventionplan.canaddfutureplan'),
          click: () => {
            this.$router.push({
              name: 'interventionPlan',
              params: {
                admissionId: this.value.admissionId,
                academicYearId: this.futureAcademicYear.id,
                interventionPlanId: this.value.interventionPlanNextYearId,
              },
            });
          },
        },
        {
          label: 'Historial de Plan Enmendado',
          icon: 'fas fa-history color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
            ]) && this.value.hasHistory,
          click: () => {
            this.$router.push({
              name: 'amendedPlanHistory',
              params: {
                interventionPlanId: this.value.hasInterventionPlan,
                admissionId: this.value.admissionId,
              },
            });
          },
        },
        {
          label: 'Informe de Logros',
          icon: 'fas fa-clipboard-list color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.ServicioRelacionadoActivo,
              AdmissionStatus.NoVigenteEgreso,
              AdmissionStatus.NoVigenteCambioServicio,
            ]) &&
            this.hasInterventionPlan &&
            this.value.isSubmitted,
          click: () => {
            this.$router.push({
              name: 'achievementSummary',
              params: {
                admissionId: this.value.admissionId,
                academicYearId: this.value.academicYearId,
                interventionPlanId: this.value.hasInterventionPlan,
              },
            });
          },
        },
        {
          label: this.hasSummary
            ? 'Ver Resumen de Resultados'
            : 'Añadir Resumen de Resultados',
          icon: 'fas fa-files-medical color-primary-1',
          visible: this.showIfContainStatus([
            AdmissionStatus.ServicioRelacionadoActivo,
            AdmissionStatus.NoVigenteEgreso,
            AdmissionStatus.NoVigenteCambioServicio,
          ]),
          click: () => {
            this.$router.push({
              name: 'resultsSummary',
              params: {
                admissionId: this.value.admissionId,
              },
            });
          },
        },
        {
          label: 'Modificar Admisión',
          icon: 'fas fa-clipboard-user color-primary-1',
          visible:
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
            ]) && this.value.academicYearId == this.currentAcademicYearData.id,
          click: this.confirm,
        },
        {
          label: 'Modificar Asignación',
          icon: 'fas fa-clipboard-user color-primary-1',
          visible:
            this.showIfContainStatus([AdmissionStatus.Asignado]) &&
            this.userHasPermissions(
              'provider.referralmanagment.specialist.assignment.update'
            ),
          click: this.assign,
        },
        {
          label: 'Ofrecimiento de Servicios Año Actual',
          icon: 'fas fa-calendar-circle-user color-primary-1',
          visible: true,
          click: () => this.serviceOfferingCurrentYear(this.value),
        },
        {
          label: this.value.hasDiagnostics
            ? 'Ver/Editar Diagnóstico'
            : 'Añadir Diagnóstico',
          icon: 'fas fa-file-alt color-primary-1',
          visible: this.canAddDiagnosisInPilot,
          click: () => this.addDiagnosis(),
        },
        {
          label: 'Traslado Interno',
          icon: 'fas fa-arrow-right color-primary-1',
          visible:
            this.hasPilotPermission &&
            this.showIfContainStatus([
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
            ]),
          click: () => {
            this.goToTransferManagement(this.value);
          },
        },
        {
          label: 'Coordinar Servicios',
          icon: 'fas fa-calendar color-primary-1',
          visible:
            (!this.isInitialReferralType || this.hasAdmissionNote) &&
            this.hasPilotPermission &&
            !this.hasCoordinatedService &&
            this.value.academicYearId === this.currentAcademicYearId &&
            this.showIfContainStatus([
              AdmissionStatus.Asignado,
              AdmissionStatus.Admitido,
              AdmissionStatus.ServicioRelacionadoActivo,
            ]),
          click: () => {
            this.goToServiceCoordination(this.value);
          },
        },
        {
          label: 'Aviso Ausencia de Plan de Intervención',
          icon: 'fas fa-file-download color-primary-1',
          visible:
            this.showInterventionPlanAbsenseCount &&
            this.value.interventionPlanAbsenceCount > 0,
          click: () => this.downloadAbsenceInterventionPlanReport(),
        },
        {
          label: 'Aviso Ausencias de Admisión',
          icon: 'fas fa-file-download color-primary-1',
          visible:
            this.showAdmissionAbsenseCount &&
            this.value.admissionAbsenceCount > 0,
          click: () => this.downloadAbsenceAdmissionPlanReport(),
        },
      ];
    },
    showAdmissionAbsenseCount() {
      return this.showIfContainStatus([AdmissionStatus.Asignado]);
    },
    showInterventionPlanAbsenseCount() {
      return this.showIfContainStatus([
        AdmissionStatus.Admitido,
        AdmissionStatus.ServicioRelacionadoActivo,
      ]);
    },
    hasCoordinatedService() {
      return this.value.coordinatedService;
    },
    prevYearIsSelected() {
      return this.value.academicYearId < this.currentAcademicYearId;
    },
    currentAcademicYearData() {
      return this.currentAcademicYear();
    },
    canAddDiagnosisInPilot() {
      return (
        this.canAddDiagnosis &&
        this.medicaidPilotCompanyIds.some((x) => x === this.value.companyId)
      );
    },
  },
  watch: {
    'moduleData.openRow': function() {
      if (this.moduleData.openRow != this.value.admissionId) {
        this.calloutOpened = false;
      }
    },
    async calloutOpened(newValue) {
      if (newValue)
        await this.persistLazyData({ openRow: this.value.admissionId }); //flag
    },
    csee(newValue, oldValue) {
      this.lastCsee = oldValue;
    },
  },
  async mounted() {
    this.csee = this.centers.find(
      (center) => center.id == this.value.providerLinkCenterId
    );
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async visitAbsence() {
      this.calloutMode = this.CALLOUT_MODES.VISIT_ABSENCE;
      this.calloutOpened = true;

      const { data } = await getAbsenceReason();
      this.absence.reasons = data;

      await _getReferralDetails(this.value.admissionId).then((res) => {
        const linkedDate = res.data.linkedDate;
        this.absence.linkedDate = linkedDate;
      });
    },
    async saveAdsence(absence) {
      await addAbsence(this.value.admissionId, absence)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Datos guardados satisfactoriamente.',
            'success'
          );
          this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
          this.calloutOpened = false;

          //increment admission absence count
          this.value.admissionAbsenceCount =
            (this.value.admissionAbsenceCount || 0) + 1;
        })
        .catch((err) => {
          if (err.response != null)
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
        });
    },
    goToDocuments(row) {
      this.$router.push({
        name: 'documentHistory',
        params: {
          studentServiceId: row.studentServiceId,
          allowAddDocument: true,
          routeTitle: 'Manejo de Referidos',
          tab: null,
          admissionId: row.admissionId,
          academicYearId: row.academicYearId,
          interventionPlanId: row.hasInterventionPlan,
          isInterventionPlanSubmitted: row.isSubmitted,
          resultSummary: row.hasSummary,
          achievementSummary: row.referralHasAchievementSummary,
          showReplacement: this.showIfContainStatus([
            AdmissionStatus.NoVigenteBajas,
            AdmissionStatus.NoVigenteAlta,
          ]),
        },
      });
    },
    goToRecord(row) {
      this.$router.push({
        name: 'ReferralRecord',
        params: { studentServiceId: row.studentServiceId },
      });
    },
    async downloadReferralSheet(row) {
      if (row.studentServiceId) {
        await _getReferralSheet(row.studentServiceId).then((response) => {
          _generalApi.ConvertToBlob(response.data);
        });
      }
    },
    showIfContainStatus(arrayStatus) {
      return arrayStatus.some((status) => status == this.value.statusId);
    },
    canSelfAssign(row) {
      if (!this.isSpecialist) {
        return (
          this.showIfContainStatus([AdmissionStatus.Vinculado]) &&
          //proveedor o especialista con permiso para asigar
          (this.userHasPermissions('provider.referral.assign') ||
            (this.userHasPermissions('provider.specialist.assignreferral') &&
              row.specialistcanSelfAssignReferralFlag))
        );
      }

      return this.showIfContainStatus([AdmissionStatus.Vinculado]);
    },
    async getDetails() {
      if (this.value.serviceId == null) {
        const { data } = await _getReferralDetails(this.value.admissionId);
        Object.assign(this.value, data);
      }
      this.tabsModel = 0;
      this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
      this.calloutOpened = !this.calloutOpened;
    },
    openVisitDetails() {
      //TODO: not implement
    },
    addPlan(row) {
      this.$router.push({
        name: 'documentHistory',
        params: {
          studentServiceId: row.studentServiceId,
          allowAddDocument: true,
          mainTitle: 'Añadir Plan',
          routeTitle: 'Seguimiento',
          companyId: row.companyId,
          admissionId: row.admissionId,
          isInterventionPlan: true,
        },
      });
    },
    downloadProvissionalRemedyRequestReport() {
      _downloadProvissionalRemedyRequestReport(
        this.value.requestNumberService
      ).then(({ data }) => {
        _generalApi.ConvertToBlob(data);
      });
    },
    onChangeCenterHandler(center) {
      if (center.id) {
        setTimeout(async () => {
          const canContinue = await this.ShowCancelModal(
            'Atención',
            '¿Está seguro que desea actualizar el centro de facturación? (0704)',
            'Sí',
            'No'
          );
          if (canContinue) {
            _updateBillingCenter({
              admissionId: this.value.admissionId,
              newBillingCenterId: center.id,
              newBillingCenterName: center.name,
              disciplineTypeId: this.value.disciplineTypeId,
              studentSieId: this.value.studentSieId,
              academicYearId: this.value.academicYearId,
              companyId: this.value.companyId,
            });
          } else this.csee = this.lastCsee;
        }, 100);
      }
    },
    approvalletterdownload(row) {
      _approvalLetterDownload(row.requestServiceId).then(({ data }) => {
        _generalApi.ConvertToBlob(data.document);
      });
    },
    createProvisionalRemedy() {
      const { studentSieId, disciplineTypeId, admissionId } = this.value;

      _createProvisionalRemedy(studentSieId, disciplineTypeId).then(
        ({ data }) => {
          if (data) {
            this.ShowCancelModal(
              'Confirmación',
              `Se encontró un servicio activo en Remedio Provisional para la misma disciplina del referido bajo la(s) solicitud(es) ${data?.requestNumber} ¿Desea identificar este referido como Aprobado en Remedio Provisional utilizando la solicitud encontrada?`,
              'Sí',
              'No'
            ).then((response) => {
              if (response) {
                _approveProvisionalRemedy({
                  admissionId: admissionId,
                  requestId: data?.id,
                  disciplineTypeId: disciplineTypeId,
                });
              }
            });
          }
        }
      );
    },
    goToReferralManagement(row) {
      this.$router.push({
        name: ADMISSION_MANAGEMENT_ROUTE_NAME,
        params: { admissionId: row.admissionId },
      });
    },
    goToTransferManagement(row) {
      this.$router.push({
        name: SPECIALIST_AVAILABILITY_CALENDAR_TRANSFER,
        params: { admissionId: row.admissionId, isTransfer: true },
      });
    },
    async goToServiceCoordination(row) {
      const { data } = await _canCoordinateService(this.value.admissionId);

      if (!data.isValid) {
        this.ShowToast('Alerta', data.errorMessage, 'error');
        return;
      }

      //Validates document
      const hasCurrentYearAdmissionDate =
        this.value.admissionDate >= this.currentAcademicYearData.fromDate &&
        this.value.admissionDate <= this.currentAcademicYearData.toDate;

      if (this.isInitialReferralType && hasCurrentYearAdmissionDate === true) {
        const response = await _getSignatureHubInfo(
          signatureHubTypes.ADMISSION_NOTE,
          this.value.admissionId
        );
        this.signatureHubInfo = response.data;

        const isSignedAdmissionNote =
          this.signatureHubInfo.documentInfo?.documentStatusId === 5; //firmado

        const refferralDocResponse = await getStudentReferralAdmision(
          this.value.admissionId
        );

        const hasRefferralDoc = refferralDocResponse.data?.referralDocs != null;

        if (!isSignedAdmissionNote && !hasRefferralDoc) {
          this.ShowToast(
            'Alerta',
            'Se debe subir nota de admisión firmada para poder coordinar servicio.',
            'error'
          );
          return;
        }
      }

      this.$router.push({
        name: SPECIALIST_AVAILABILITY_CALENDAR_COORDINATION,
        params: {
          admissionId: row.admissionId,
          isCoordination: this.hasAdmissionNote && this.hasCoordinatedService,
        },
      });
    },
    async confirm() {
      const { data } = await getAdmissionInfo(this.value.admissionId);

      if (data.maxDate) data.maxDate = new Date(Date.parse(data.maxDate));

      if (data.minDate) data.minDate = new Date(Date.parse(data.minDate));

      if (data.admissionDate)
        data.admissionDate = new Date(Date.parse(data.admissionDate));

      this.admissionInfo = data;

      this.calloutMode = this.CALLOUT_MODES.CONFIRM_ADMISSION;
      this.calloutOpened = true;
    },
    async serviceOfferingCurrentYear(item) {
      const { academicYearId, studentSieId, disciplineTypeId } = item;
      this.calloutMode = this.CALLOUT_MODES.SERVICE_OFFERING_CURRENTYEAR;
      this.calloutOpened = true;
      const { data } = await getStudentServiceOffering({
        academicYearId,
        studentSieId,
        disciplineTypeId,
      });
      item.serviceOfferingData = data;
      this.tabsModel = 3;
      this.$forceUpdate();
    },
    async saveConfirm(data) {
      if (this.hasPilotPermission) {
        try {
          await savePartialAdmission(data);
          this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
          this.calloutOpened = false;

          if (data.goToServiceCoordination === true) {
            this.goToServiceCoordination(this.value);
          } else {
            this.goToAddAdmissionNote();
          }
        } catch (error) {
          this.ShowToast(
            'Operación fallida',
            error.response.data?.message ||
              'No se pudo completar la operación.',
            'error'
          );
        }
        return;
      }
      this.admitReferral(data);
    },
    async admitReferral(data) {
      await confirmAdmission(data)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Datos guardados satisfactoriamente.',
            'success'
          );

          this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
          this.calloutOpened = false;

          this.$emit('refreshReferralGrid');
        })
        .catch((error) => {
          this.ShowToast(
            'Operación fallida',
            error.response.data?.message ||
              'No se pudo completar la operación.',
            'error'
          );
        });
    },
    async partialSaveAndGoToCalendar(data) {
      try {
        await savePartialAdmission(data);
        this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
        this.calloutOpened = false;
        this.goToAddAdmissionNote();
      } catch (error) {
        this.ShowToast(
          'Operación fallida',
          error.response.data?.message || 'No se pudo completar la operación.',
          'error'
        );
      }
    },
    async updateConfirm(data) {
      await updateAdmission(data)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'La admisión ha sido actualizada exitosamente.',
            'success'
          );
          this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
          this.calloutOpened = false;

          this.$emit('refreshReferralGrid');
        })
        .catch(() => {
          this.ShowToast(
            'Operación fallida',
            'No se pudo completar la operación.',
            'error'
          );
        });
    },
    async assign() {
      const referral = this.value;
      const specialistId =
        referral.admissionSpecialist > 0 ? referral.admissionSpecialist : 0;

      this.calloutMode = this.CALLOUT_MODES.ASSIGN_SPECIALIST;
      this.calloutOpened = true;

      const { data } = await getSpecialistsForAssignment(
        referral.admissionId,
        referral.locationFiscalYearId,
        referral.studentSieId,
        specialistId
      );
      this.assignSpecialists = data;
    },
    async saveAssignment(specialistId) {
      await _saveAssignment({
        admissionId: this.value.admissionId,
        specialistId,
      });

      this.ShowToast(
        'Proceso Exitoso',
        'Datos guardados satisfactoriamente.',
        'success'
      );
      this.value.statusId = AdmissionStatus.Asignado;
      this.value.status = 'Asignado';

      this.value.serviceId = null;

      this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
      this.calloutOpened = false;

      this.$emit('refreshReferralGrid');
    },
    goToAddAdmissionNote() {
      this.$router.push({
        name: 'admissionNote',
        params: { admissionId: this.value.admissionId },
      });
    },
    async onPrint(item) {
      const { studentSieId, disciplineTypeId, academicYearId } = item;
      await _generalApi.downloadWeeklycompliance({
        academicYearId: academicYearId,
        studentSieId: studentSieId,
        serviceId: disciplineTypeId,
      });
    },
    async downloadAbsenceInterventionPlanReport() {
      await _generalApi.downloadInterventionPlanAbsences(
        this.value.admissionId
      );
    },
    async downloadAbsenceAdmissionPlanReport() {
      await _generalApi.downloadAdmissionAbsences(this.value.admissionId);
    },
    addDiagnosis() {
      this.tabsModel = 1;
      this.calloutMode = this.CALLOUT_MODES.DIAGNOSIS;
      this.calloutOpened = true;
    },
    onDiagnosisCancel() {
      this.tabsModel = 0;
      this.calloutMode = this.CALLOUT_MODES.STUDENT_INFORMATION;
      this.calloutOpened = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.col-balloon {
  min-width: 265px !important;
}

@media (max-width: 680px) {
  .responsive-dropdown ::v-deep {
    button.dropdown-toggle {
      width: 100%;
    }
  }
}
::v-deep .fa-calendar-circle-user {
  background-image: url('../../../../assets/img/calendar-user-circle-green.svg');
  width: 19px;
  height: 19px;
  position: relative;
  top: 3px;
  background-repeat: no-repeat;
  background-position: center;
  margin-right: 0px !important;
}

::v-deep .fa-calendar-circle-user + span {
  margin-left: 25px;
}
</style>
